import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import Footer from '../../components/Footer';
import './TeachersPage.css';

const isBelkina = (teacher) => {
    const last = String(teacher?.last_name || '').trim().toLowerCase();
    const first = String(teacher?.name || '').trim().toLowerCase();
    const full = `${last} ${first}`.trim();
    return last.includes('белкин') || full.includes('белкин');
};

const TeacherCard = ({ teacher, index = 0, featured = false }) => {
    const displayName = formatUserDisplayName(teacher);
    const short = (teacher.short_description || '').trim();
    const profileHref = `/public/profile/${teacher.id}`;

    return (
        <article
            className={`teacher-card${featured ? ' teacher-card--featured' : ''}`}
            style={{ '--teacher-delay': `${Math.min(index, 11) * 45}ms` }}
        >
            <Link
                to={profileHref}
                className="teacher-card-photo"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${displayName} — подробнее`}
            >
                {teacher.photo_url ? (
                    <img src={teacher.photo_url} alt={displayName} loading="lazy" />
                ) : (
                    <div className="teacher-card-photo-placeholder" aria-hidden="true" />
                )}
            </Link>

            <div className="teacher-card-body">
                <h2 className="teacher-card-name">{displayName}</h2>
                <div className="teacher-card-divider" aria-hidden="true" />
                {short ? (
                    <p className="teacher-card-short">{short}</p>
                ) : (
                    <p className="teacher-card-short teacher-card-short-empty">&nbsp;</p>
                )}
                <Link
                    to={profileHref}
                    className="teacher-card-more"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Подробнее
                </Link>
            </div>
        </article>
    );
};

const TeachersPage = () => {
    useIframeCommunication();

    const { data: teachers, isLoading, error } = useQuery('publicTeachers', async () => {
        const response = await axios.get(`${API_BASE_URL}/users/teachers`);
        return response.data;
    });

    const list = useMemo(() => (Array.isArray(teachers) ? teachers : []), [teachers]);
    const staffList = useMemo(
        () => list.filter((t) => !t.is_external_staff),
        [list]
    );
    const externalList = useMemo(
        () => list.filter((t) => Boolean(t.is_external_staff)),
        [list]
    );
    const leader = useMemo(() => staffList.find(isBelkina) || list.find(isBelkina) || null, [staffList, list]);

    if (isLoading) {
        return (
            <div className="teachers-page">
                <div className="teachers-loading">
                    <div className="spinner" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="teachers-page">
                <p className="teachers-error">Не удалось загрузить список преподавателей</p>
            </div>
        );
    }

    return (
        <div className="teachers-page">
            {leader && (
                <section className="teachers-section" aria-labelledby="teachers-leader-title">
                    <h2 id="teachers-leader-title" className="teachers-section-title">
                        Руководитель
                    </h2>
                    <div className="teachers-leader">
                        <TeacherCard teacher={leader} featured />
                    </div>
                </section>
            )}

            <section className="teachers-section" aria-labelledby="teachers-list-title">
                <h2 id="teachers-list-title" className="teachers-section-title">
                    Преподаватели
                </h2>
                <div className="teachers-grid">
                    {staffList.map((teacher, index) => (
                        <TeacherCard key={teacher.id} teacher={teacher} index={index} />
                    ))}
                </div>

                {staffList.length === 0 && (
                    <p className="teachers-empty">Пока нет опубликованных преподавателей</p>
                )}
            </section>

            {externalList.length > 0 && (
                <section className="teachers-section" aria-labelledby="teachers-external-title">
                    <h2 id="teachers-external-title" className="teachers-section-title">
                        Внештатные сотрудники
                    </h2>
                    <div className="teachers-grid">
                        {externalList.map((teacher, index) => (
                            <TeacherCard key={teacher.id} teacher={teacher} index={index} />
                        ))}
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
};

export default TeachersPage;
